// @ts-strict-ignore
import type { SerializedStyles } from "@emotion/react";
import type { HTMLProps, ReactNode } from "react";

import { Box } from "../Box";
import { Checkbox } from "../Checkbox";
import { Radio } from "../Radio";

import * as styles from "./SelectableTile.styles";

interface Props extends HTMLProps<HTMLInputElement> {
  /**
   * Triggers the checked attribute on the checkbox or radio button
   */
  checked: boolean;
  /**
   * Displays a radio button instead of a checkbox
   *
   * @default false
   */
  isRadio?: boolean;
  /**
   * Affects the padding of any content passed into SelectableTile
   */
  contentWrapperStyles?: SerializedStyles;
  /**
   * Customizes the component's Box styles.
   * Only takes into effect when the checked prop is false
   */
  boxStyle?: SerializedStyles;
  /**
   * Centres the checkbox or radio button instead of being right-aligned by default
   *
   * @default false
   */
  isCheckboxCentered?: boolean;
  /**
   * Aligns the checkbox/radio to the left of the content
   *
   * @default false
   */
  isInline?: boolean;
  /**
   * Specifies the location of the checkbox/radio when using the inline version
   *
   * @default "baseline"
   */
  inlineLocation?: "baseline" | "center";
  /**
   * Removes hardcoded height and inherits from content
   *
   * @default false
   */
  isContentHeight?: boolean;
  /**
   * Configures checkbox/radio container to take 100% height of its parent
   *
   * @default false
   */
  isFullHeight?: boolean;
  /**
   * When true, sets checkbox content to be full width
   */
  isContentFullWidth?: boolean;
  /**
   * Content to render inside the checkable tile
   *
   * @default undefined
   */
  children?: ReactNode;
  "aria-label": string;
}

/**
 * @deprecated Use `SelectableTile` from `@fullscript/aviary-web` instead.
 */
const SelectableTile = ({
  isRadio = false,
  inlineLocation = "baseline",
  isFullHeight = false,
  isContentFullWidth = false,
  ...props
}: Props) => {
  const {
    checked,
    children,
    contentWrapperStyles,
    boxStyle,
    isInline,
    isContentHeight,
    isCheckboxCentered,
    disabled,
    ...rest
  } = props;

  const buildWrapperStyles = () => {
    return [
      styles.wrapper.default,
      !isRadio && styles.wrapper.checkbox,
      isInline && styles.inlineInput,
      isCheckboxCentered && styles.checkboxCentered,
      isFullHeight && styles.fullHeight,
      checked && styles.wrapper.checked,
      disabled && styles.wrapper.disabled,
      boxStyle,
    ];
  };

  const labelStyles = [
    styles.labelWrapper,
    isRadio && styles.radioLabelOverride,
    isInline && styles.inlineLabel[inlineLocation],
  ];

  const contentStyles = [
    styles.contentSection.base,
    isInline && styles.contentSection.inlineContent,
    !isContentHeight && !isFullHeight && styles.contentSection.heights,
    contentWrapperStyles,
    disabled && styles.contentSection.disabled,
  ];

  const renderCheckbox = () => {
    if (isRadio) {
      return (
        <Radio checked={checked} disabled={disabled} labelStyle={labelStyles} {...rest}>
          <div css={contentStyles}>{children}</div>
        </Radio>
      );
    }

    return (
      <Checkbox
        checked={checked}
        disabled={disabled}
        labelStyle={labelStyles}
        isContentFullWidth={isContentFullWidth}
        {...rest}
      >
        <div css={contentStyles}>{children}</div>
      </Checkbox>
    );
  };

  return (
    <Box padding="none" layoutStyles={buildWrapperStyles()}>
      {renderCheckbox()}
    </Box>
  );
};

export { SelectableTile };
